import { defineStore } from 'pinia';
import { useAuthStore } from '@/stores/auth.ts';
import { useSessionStateStore } from '@/stores/session-state.ts';
import { SessionInteractionAction, type SessionInteractionPayload } from '@pokerly/contracts/events/common';
import type { SessionInteractionCallback } from '@/services/pokerly-ws/callbacks.js';

/**
 * Session interactions store - manages emoji throws and other session interactions.
 */
export const useSessionInteractionsStore = defineStore('session-interactions', () => {
    const interactionCallbacks: Array<(interaction: SessionInteractionPayload) => void> = [];

    // --- Event Handler (to be registered with PokerlyWs) ---

    const onSessionInteraction: SessionInteractionCallback = (event) => {
        const authStore = useAuthStore();
        if (event.action !== SessionInteractionAction.EmojiThrow) return;
        if (authStore.user?.id === event.payload.fromUserId) return;
        interactionCallbacks.forEach((cb) => cb(event));
    };

    // --- Actions ---

    function sendEmojiThrowInteraction(toUserId: string, emoji: string) {
        const authStore = useAuthStore();
        const fromUserId = authStore.user?.id;
        if (!fromUserId) return;
        sendInteraction({
            action: SessionInteractionAction.EmojiThrow,
            payload: { fromUserId, toUserId, emoji },
        });
    }

    function sendInteraction(interaction: Omit<SessionInteractionPayload, 'sessionId'>) {
        const sessionState = useSessionStateStore();
        sessionState.getWs().sendInteraction(interaction);
    }

    function onInteractionReceived(cb: (interaction: SessionInteractionPayload) => void) {
        interactionCallbacks.push(cb);
    }

    function removeInteractionCallback(cb: (interaction: SessionInteractionPayload) => void) {
        const index = interactionCallbacks.indexOf(cb);
        if (index > -1) {
            interactionCallbacks.splice(index, 1);
        }
    }

    function $reset() {
        interactionCallbacks.length = 0;
    }

    return {
        // Event handler (for ws registration)
        onSessionInteraction,

        // Actions
        sendEmojiThrowInteraction,
        sendInteraction,
        onInteractionReceived,
        removeInteractionCallback,

        // Reset
        $reset,
    };
});
